import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Map, { Source, Layer, Marker } from "react-map-gl";
import {
  Button,
  Collapse,
  Col,
  Row,
  Table,
  Modal,
  Form,
  Input,
  Switch,
  List,
  Tooltip,
  Spin,
  notification,
} from "antd";
import {
  stateFilter,
  stateCode,
  safeLabels,
  landCoverTypeLabels,
  priorityLabels,
  columns,
  safePractices,
  withinFloodplainPractices,
  outsideFloodplainPractices,
  waterBufferPractices,
  sqmToAcres,
} from "./utils/helperFunctions";
import {
  TbHexagonLetterC,
  TbHexagonLetterP,
  TbHexagonLetterR,
  TbHexagonLetterS,
  TbSquareLetterC,
  TbSquareLetterG,
} from "react-icons/tb";
import * as htmlToImage from "html-to-image";
import { LuLogIn, LuMap, LuPrinter, LuSave } from "react-icons/lu";
import polylabel from "polylabel";
import { geoArea } from "d3-geo";
import bbox from "@turf/bbox";
import center from "@turf/center";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import { ModuleContext } from "../../context/Context";
import Header from "../../Components/Header";
import Footer from "../../Components/Footer";
import hostURL from "../../Common/constants";
import PracticeCard from "../PracticeList/Components/PracticeCard";
import "./style.css";
import PrintHelp from "./Components/PrintHelp";
import { PrinterOutlined } from "@ant-design/icons";
import { MdOutlineCancel } from "react-icons/md";
import { useSelector } from "react-redux";
import {
  calculatePracticeScore,
  getMatchLabel,
} from "../../utils/practiceScoringUtils";
import { crepColorMap } from "../FieldReport/utils/helperFunctions";

const FieldPrint = ({ userFavoritePractices }) => {
  const [items, setItems] = useState([]);
  const [printList, setPrintList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [soilData, setSoilData] = useState(null);
  const [fieldBasics, setFieldBasics] = useState(null);
  const [fieldFeatureData, setFieldFeatureData] = useState(null);
  const [fieldAddress, setFieldAddress] = useState("Not Specified");
  const [fieldSize, setFieldSize] = useState(null);
  const [fieldRentalRate, setFieldRentalRate] = useState(null);
  const [soilTableData, setSoilTableData] = useState(null);
  const [baseLayer, setBaseLayer] = useState(
    "mapbox://styles/mapbox/satellite-streets-v12"
  );
  const [layerSelectOpen, setLayerSelectOpen] = useState(false);
  const [soilLayerOn, setSoilLayerOn] = useState(true);
  const [safeLayerOn, setSafeLayerOn] = useState(false);
  const [floodplainLayerOn, setFloodplainLayerOn] = useState(false);
  const [waterLayerOn, setWaterLayerOn] = useState(false);
  const [interactiveLayerIds, setInteractiveLayerIds] = useState([]);
  const [filter, setFilter] = useState([]);
  const [highlightFeatureId, setHighlightFeatureId] = useState(null);
  const [selectedFips, setSelectedFips] = useState(null);
  const [selectedPracticeList, setselectedPracticeList] = useState(null);
  const [countyListData, setCountyListData] = useState(null);
  const [selectedTopPracticeList, setselectedTopPracticeList] = useState(null);
  const [filteredPractices, setFilteredPractices] = useState(null);
  const [eligiblePractices, setEligiblePractices] = useState(null);
  const [eligiblePracticesData, setEligiblePracticesData] = useState([]);
  const [eligiblePracticesPriorities, setEligiblePracticesPriorities] =
    useState([]);
  const [eligiblePracticesPrioritiesRaw, setEligiblePracticesPrioritiesRaw] =
    useState([]);
  const [isSavePopOpen, setIsSavePopOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [serviceCenterList, setServiceCenterList] = useState([]);
  const [preferredPractice, setPreferredPractice] = useState(null);
  const [preferredSignups, setPreferredSignups] = useState({
    G: true,
    C: true,
  });
  const [preferredIncentives, setPreferredIncentives] = useState({
    S: false,
    R: false,
    P: false,
    C: "5",
  });
  const mapRef = useRef();
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const authToken = secureLocalStorage.getItem("token");
  const fieldDetails = secureLocalStorage.getItem("field-details");
  console.log("FIELD DETAILS: ", fieldDetails);
  const allPractices = useSelector((state) => state.practice.allPractices);
  const { landCoverSelections, prioritySelections, rankingScore } = useSelector(
    (state) => state.filter
  );
  const waterData = {
    features: fieldDetails?.waterBuffer?.rows.map((row) => ({
      type: "Feature",
      geometry: JSON.parse(row.geometry),
    })),
  };

  const floodplainData = {
    features: fieldDetails?.floodPlain?.rows.map((row) => ({
      type: "Feature",
      geometry: JSON.parse(row.geometry),
    })),
  };

  const safeData = {
    features: fieldDetails?.safeArea?.rows.map((row) => ({
      type: "Feature",
      geometry: JSON.parse(row.geometry),
    })),
  };

  const hydroData = {
    features: fieldDetails.hydricSoil.rows.map((row) => {
      return {
        type: "Feature",
        geometry: JSON.parse(row.geometry),
        id: row.gid,
        properties: { gridcode: row.gridcode, area: row.area }
      };
    })
  };

  console.log("👀 fieldBasics:", fieldBasics);
  console.log("📐 fieldSize:", fieldSize);
  console.log("💰 fieldRentalRate:", fieldRentalRate);
  console.log("🧱 soilTableData:", soilTableData);
  console.log("📞 serviceCenterList:", serviceCenterList);

  const [api] = notification.useNotification();

  const openNotificationWithIcon = (type, title, message) => {
    api[type]({
      message: title,
      description: message,
    });
  };

  const saveSearchPopClose = () => {
    form.resetFields();
    setIsSavePopOpen(false);
  };

  const storeSearchData = async (values, sroute) => {
    form.resetFields();
    setIsSavePopOpen(false);
    let imageDataURL = null;
    setLoading(true);

    await htmlToImage
      .toPng(document.getElementById("fieldReportMap"), { allowTaint: true })
      .then(function (dataurl) {
        imageDataURL = dataurl;
      });

    const searchedData = {
      selectedState: fieldBasics.state,
      selectedCounty: fieldBasics.county,
      landCoverSelections: landCoverSelections,
      prioritySelections: prioritySelections,
      rankingScore: rankingScore,
      mapimage: imageDataURL,
      data: fieldDetails,
    };

    const formData = {
      userId: secureLocalStorage.getItem("userRef"),
      route: window.location.href,
      formData: JSON.stringify(searchedData),
      mySearchName: values.SaveKey,
      stype: "checkEligibility",
    };

    setLoading(true);
    fetch(hostURL + "api/usersearchdata", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: authToken,
      },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((json) => {
        openNotificationWithIcon(
          "success",
          "Success",
          "Saved Your Search Criteria"
        );
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        openNotificationWithIcon("error", "Error", "Try Again Later");
      });
  };

  const getFieldAddress = async () => {
    const fieldGeometry = JSON.parse(fieldDetails.countyRate.rows[0].geometry);
    const centerPoint = center(fieldGeometry);
    const [lng, lat] = centerPoint.geometry.coordinates;
    setLoading(true);
    const response = await axios.get(
      "https://api.mapbox.com/search/geocode/v6/reverse?longitude=" +
        lng +
        "&latitude=" +
        lat +
        "&access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
    );
    if (response.data) {
      setTimeout(() => {
        setLoading(false);
      }, 6000);
      setFieldAddress(response.data.features[0].properties.full_address);
    }
  };

  const getServiceCenter = async (queryState, queryCounty) => {
    const response = await axios.post(hostURL + "api/field/service-center", {
      state: queryState,
      county: queryCounty,
    });
    if (response) {
      const fsaServiceCenters = response.data.serviceCenter.rows.filter(
        (item) => item.service_center.includes("Farm Service Agency")
      );
      setServiceCenterList(fsaServiceCenters);
    }
  };

  const handleLayerSelect = () => {
    setLayerSelectOpen(!layerSelectOpen);
  };

  const getMarkerLocation = (geometry) => {
    let output = [];
    if (geometry.type === "Polygon") {
      output = polylabel(geometry.coordinates, 0.000001);
    } else {
      let maxArea = 0,
        maxPolygon = [];
      for (let i = 0, l = geometry.coordinates.length; i < l; i++) {
        const p = geometry.coordinates[i];
        const area = geoArea({ type: "Polygon", coordinates: p });
        if (area > maxArea) {
          maxPolygon = p;
          maxArea = area;
        }
      }
      output = polylabel(maxPolygon, 0.000001);
    }
    return output;
  };

  const onMouseMove = (e) => {
    if (e.features.length) {
      const feature = e.features[0];
      setFilter(["in", "gid", feature.id]);
      setHighlightFeatureId(feature.id);
    }
  };

  useEffect(() => {
    if (!fieldDetails) return;

    const basics = fieldDetails?.countyRate?.rows?.[0];
    if (!basics) return;

    setFieldBasics(basics);
    setFieldSize(basics?.area * 0.000247105);
    setFieldFeatureData(basics?.geometry ? JSON.parse(basics.geometry) : null);

    const sortedSoils = [...(fieldDetails?.fieldRate?.rows || [])].sort(
      (a, b) => b.area - a.area
    );

    const transformedRows = sortedSoils.map((soil) => ({
      key: soil.gid,
      soil_type: soil.musym,
      rental_rate: `$${soil.srr}/acre`,
      acreage: Math.round(soil.area * 0.000247105 * 100) / 100,
    }));

    setSoilTableData(transformedRows);

    getFieldAddress();
    getServiceCenter(basics.state, basics.county);
  }, []);

  useEffect(() => {
    if (fieldBasics) {
      setLoading(true);
      fetch(hostURL + "api/counties?state=" + stateCode[fieldBasics.state])
        .then((response) => response.json())
        .then((json) => {
          setCountyListData(json);
          setLoading(false);
        })
        .catch((error) => console.error(error));
    }
  }, [fieldBasics]);

  useEffect(() => {
    if (countyListData && fieldBasics) {
      let countyDataArray = countyListData.filter(
        (item) => item.cname === fieldBasics.county
      );
      let assetData =
        countyDataArray.length > 0 ? countyDataArray[0].assetdata : null;
      let topPracticeList = assetData
        ? assetData.replace('"', "").split(",")
        : null;
      setselectedTopPracticeList(topPracticeList);
    }
  }, [countyListData, fieldBasics]);

  useEffect(() => {
    if (!soilTableData) return;

    const rateBySoilTypes = soilTableData.map((soilType) => {
      const areaInAcre = soilType.acreage || 0; // use acreage directly since it's already converted
      const rate =
        typeof soilType.rental_rate === "string"
          ? parseFloat(soilType.rental_rate.replace(/[^0-9.]/g, ""))
          : 0;
      return areaInAcre * rate;
    });

    const totalRentalRate = Math.round(
      rateBySoilTypes.reduce((a, b) => a + b, 0)
    );

    setFieldRentalRate(totalRentalRate);
  }, [soilTableData]);

  useEffect(() => {
    if (mapRef.current) {
      const [minLng, minLat, maxLng, maxLat] = bbox(fieldFeatureData);
      mapRef.current.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: 40, duration: 1000 }
      );
    }
  }, [fieldFeatureData]);

  const filteredByPriorityAndLandCover = useMemo(() => {
    return allPractices.filter((practice) => {
      if (
        !Array.isArray(practice.priorities) ||
        !Array.isArray(practice.landcover)
      )
        return false;

      const matchesPriority =
        prioritySelections.length === 0 ||
        prioritySelections.some((selected) =>
          practice.priorities?.some((p) => p.name === selected)
        );

      const matchesLandCover =
        landCoverSelections.length === 0 ||
        landCoverSelections.some((selected) =>
          practice.landcover?.includes(selected)
        );

      return matchesPriority && matchesLandCover;
    });
  }, [allPractices, prioritySelections, landCoverSelections]);

  const waterBufferFilteredPractices = useMemo(() => {
    const fieldHasWaterBuffer = waterData?.features?.length > 0;

    return filteredByPriorityAndLandCover.filter((practice) => {
      const practiceCode = practice.practiceNumber;
      const requiresWaterBuffer =
        waterBufferPractices.hasOwnProperty(practiceCode);

      if (requiresWaterBuffer && !fieldHasWaterBuffer) return false;
      return true;
    });
  }, [filteredByPriorityAndLandCover, waterData]);

  const floodplainFilteredPractices = useMemo(() => {
    const fieldHasFloodplain = floodplainData?.features?.length > 0;

    return waterBufferFilteredPractices.filter((practice) => {
      const practiceCode = practice.practiceNumber;
      const mustBeInFloodplain =
        withinFloodplainPractices.includes(practiceCode);
      const mustBeOutsideFloodplain =
        outsideFloodplainPractices.includes(practiceCode);

      if (mustBeInFloodplain && !fieldHasFloodplain) return false;
      if (mustBeOutsideFloodplain && fieldHasFloodplain) return false;

      return true;
    });
  }, [waterBufferFilteredPractices, floodplainData]);

  const safeFilteredPractices = floodplainFilteredPractices.filter(
    (practice) => {
      const fieldHasSafeZone = safeData?.features?.length > 0;
      const safePracticeTitles = Object.values(safePractices);
      const isSafePractice = safePracticeTitles.includes(practice.title);

      if (isSafePractice && !fieldHasSafeZone) {
        return false;
      }

      return true;
    }
  );

  const hydroFilteredPractices = safeFilteredPractices.filter(
    (practice) => {
      const fieldTotalArea = hydroData?.features?.map((feature) => 
        feature.properties.area
      ).reduce((a, b) => a + b, 0);
      const fieldHydricArea = hydroData?.features?.map((feature) => {
        if (feature.properties.gridcode > 50) {
          return feature.properties.area;
        } else {
          return 0;
        }
      }).reduce((a, b) => a + b, 0);
      const fieldHasHydricSoil = fieldHydricArea / fieldTotalArea > 0.5 ? true : false;

      if (practice.practiceNumber === "CP23" && !fieldHasHydricSoil) {
        return false;
      } else {
        return true;
      }
    }
  );

  const scoredAndSortedPractices = useMemo(() => {
    return hydroFilteredPractices
      .map((practice) => {
        const score = calculatePracticeScore(practice, rankingScore);
        const { scoreLabel, scoreClassName } = getMatchLabel(
          score,
          prioritySelections.length
        );

        return {
          ...practice,
          score,
          scoreLabel,
          scoreClassName,
        };
      })
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        const getCPNum = (p) => {
          const match = p.practiceNumber?.match(/\d+/);
          return match ? parseInt(match[0], 10) : 0;
        };

        return getCPNum(a) - getCPNum(b);
      });
  }, [hydroFilteredPractices, rankingScore, prioritySelections]);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  function titleCase(str) {
    return str
      ?.toLowerCase()
      .split(" ")
      .map(function (word) {
        return word.replace(word[0], word[0].toUpperCase());
      })
      .join(" ");
  }

  const date = new Date();
  let day = ("0" + date.getDate()).slice(-2);
  let month = ("0" + (date.getMonth() + 1)).slice(-2);
  let year = date.getFullYear() - 2000;
  let currentDate = `${month}/${day}/${year}`;

  useEffect(() => {
    if (!fieldDetails) {
      navigate("/");
    }
  }, [fieldDetails, navigate]);

  if (!fieldDetails) return null;

  return (
    <>
      <Spin spinning={loading}>
        <Header noNavigation={"print"} />
        <div className="print report">
          <div className="report-head-container">
            <div style={{ display: "flex", flexDirection: "column" }}>
              <h2
                className={printList ? "print report-header" : "report-header"}
              >
                CRP Field Eligibility Report:
                <span
                  style={{ fontWeight: "normal" }}
                >{` ${fieldBasics?.county}, ${fieldBasics?.state}`}</span>
              </h2>
              <div className="basic-info-container">
                <div className="report-basic-information">
                  <p>
                    <b>FIPS Code: </b>
                    {fieldBasics?.fips} | <b>Field Size: ≈ </b>
                    {Math.floor(fieldSize)} acre |{" "}
                    {/* <b>County Average Rental Rate*: </b>$
                    {Math.round(fieldBasics?.srr2024 * 100) / 100}/acre */}
                  </p>
                  <p>
                    <b>SAFE Zone(s): </b>
                    {fieldDetails.safeArea.rows.length === 0
                      ? "No"
                      : fieldDetails.safeArea.rows
                          .map((item) => safeLabels[item.code])
                          .join(", ")}{" "}
                    | <b>Floodplain: </b>
                    {fieldDetails.floodPlain.rows.length ? "Yes" : "No"} |{" "}
                    <b>Selected Priorities: </b>
                    {prioritySelections.length
                      ? prioritySelections.map((priority, index) => {
                          if (index === 0) {
                            return priorityLabels[priority];
                          } else {
                            return ", " + priorityLabels[priority];
                          }
                        })
                      : "No priority selected, please go back to explore CRP practices"}
                  </p>
                  <p>
                    <b>Top 3 soil types: </b>
                    {soilTableData?.slice(0, 3).map((soil, index) => (
                      <span key={soil.key}>
                        {`${soil.soil_type} (${soil.acreage} acres @ ${soil.rental_rate})`}
                        {index < 2 && ", "}
                      </span>
                    ))}
                    . To understand these soil type codes, please find the
                    details{" "}
                    <a
                      href="https://www.nrcs.usda.gov/publications/Map%20Unit%20Description%20%28Brief%2C%20Generated%29%20Query%20by%20Soil%20Survey%20Area%20and%20Map%20Unit%20%28All%20Components%29.html"
                      target="_blank"
                      style={{ color: "maroon" }}
                      rel="noreferrer"
                    >
                      here
                    </a>
                    .
                  </p>

                  <p>
                    <b>Total weighted average rental rate estimate: </b>
                    <b> ${fieldRentalRate}</b>
                    <sup>[1]</sup>
                  </p>
                  <p>
                    <b>
                      Total weighted average rental rate per acre estimate:{" "}
                    </b>
                    <b>
                      ${Math.round(fieldRentalRate / fieldSize)}
                      /acre
                    </b>
                    *
                  </p>
                </div>
              </div>
            </div>
            <div className="report-head-btn-container">
              <Button
                onClick={() => window.print()}
                className="print-button filter-button"
              >
                <b>
                  <PrinterOutlined /> Print
                </b>
              </Button>

              <Button onClick={() => window.close()} className="close-button ">
                <b className="close-text">
                  <MdOutlineCancel /> <span>Close</span>
                </b>
              </Button>
            </div>
          </div>

          <hr className="report-hr" />
          <div className="report-map-container">
            <h3 className={"report-section-header"}>Field Boundary:</h3>
            <div className="print report-map-inner-container">
              <Map
                id="fieldReportMap"
                ref={mapRef}
                mapboxAccessToken="pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                initialViewState={{
                  longitude: -91,
                  latitude: 36,
                  preserveDrawingBuffer: true,
                  zoom: 4,
                }}
                mapStyle={baseLayer}
                interactiveLayerIds={interactiveLayerIds}
                onMouseMove={onMouseMove}
              >
                <Tooltip placement="bottom" title="Layer Tool">
                  <Button
                    className="report-layer-select"
                    onClick={handleLayerSelect}
                  >
                    <LuMap strokeWidth={3} />
                  </Button>
                </Tooltip>
                {layerSelectOpen && (
                  <div className="report-layer-options">
                    <div className="base-layers">
                      <p>
                        <b>Base Layers</b>
                      </p>
                      <p>
                        <img
                          alt="thumbnail for light view of map"
                          className="layer-thumbnail"
                          src="https://api.mapbox.com/styles/v1/mapbox/light-v11/static/-88.81,33.45,7.0,0,0/50x50?access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                        />
                        <Switch
                          className="report-layer-switch"
                          checked={
                            baseLayer ===
                            "mapbox://styles/mapbox/satellite-streets-v12"
                          }
                          checkedChildren="Satellite"
                          unCheckedChildren="light"
                          defaultChecked
                          onChange={(checked) => {
                            if (checked) {
                              setBaseLayer(
                                "mapbox://styles/mapbox/satellite-streets-v12"
                              );
                            } else {
                              setBaseLayer("mapbox://styles/mapbox/light-v11");
                            }
                          }}
                        />
                        <img
                          alt="thumbnail for light view of map"
                          className="layer-thumbnail"
                          src="https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/-88.81,33.45,7.0,0,0/50x50?access_token=pk.eyJ1IjoiY2h1Y2swNTIwIiwiYSI6ImNrMDk2NDFhNTA0bW0zbHVuZTk3dHQ1cGUifQ.dkjP73KdE6JMTiLcUoHvUA"
                        />
                      </p>
                    </div>
                    <hr />
                    <div className="supporting-layers">
                      <p>
                        <b>Supporting Layers</b>
                      </p>
                      <p>
                        <input
                          type="checkbox"
                          checked={soilLayerOn}
                          onClick={(e) => {
                            if (e.currentTarget.checked) {
                              setSoilLayerOn(true);
                            } else {
                              setSoilLayerOn(false);
                            }
                          }}
                        />
                        Soil Types
                        <div className="report-legend-soil"></div>
                      </p>
                      <p>
                        <input
                          type="checkbox"
                          checked={safeLayerOn}
                          onClick={(e) => {
                            if (e.currentTarget.checked) {
                              setSafeLayerOn(true);
                            } else {
                              setSafeLayerOn(false);
                            }
                          }}
                        />
                        SAFE Boundaries
                        <div className="report-legend-safe"></div>
                      </p>
                      <p>
                        <input
                          type="checkbox"
                          checked={floodplainLayerOn}
                          onClick={(e) => {
                            if (e.currentTarget.checked) {
                              setFloodplainLayerOn(true);
                            } else {
                              setFloodplainLayerOn(false);
                            }
                          }}
                        />
                        Floodplains
                        <div className="report-legend-floodplain"></div>
                      </p>
                      <p>
                        <input
                          type="checkbox"
                          checked={waterLayerOn}
                          onClick={(e) => {
                            if (e.currentTarget.checked) {
                              setWaterLayerOn(true);
                            } else {
                              setWaterLayerOn(false);
                            }
                          }}
                        />
                        Water Features
                        <div className="report-legend-water"></div>
                      </p>
                    </div>
                  </div>
                )}
                <Source
                  id="map-source"
                  type="vector"
                  url="mapbox://chuck0520.5pq9omti"
                  maxzoom={22}
                  minzoom={0}
                >
                  <Layer
                    id="county-boundary"
                    source-layer="c_08mr23-1pp4eg"
                    type="line"
                    paint={{
                      "line-color": "#800000",
                      "line-width": 1,
                      "line-opacity": 0.4,
                    }}
                    minzoom={0}
                    maxzoom={22}
                    filter={stateFilter}
                  />
                </Source>
                <Source
                  id="field-source"
                  type="geojson"
                  data={fieldFeatureData}
                >
                  <Layer
                    id="field"
                    type="line"
                    paint={{
                      "line-color": "white",
                      "line-width": 2,
                    }}
                  />
                </Source>
                {soilLayerOn && (
                  <Source id={"soil-source"} type="geojson" data={soilData}>
                    <Layer
                      id={"soil-layer"}
                      type="fill"
                      paint={{
                        "fill-outline-color": "white",
                        "fill-color": "transparent",
                      }}
                    />
                    <Layer
                      id={"soil-layer-highlight"}
                      type="fill"
                      paint={{
                        "fill-color": "purple",
                        "fill-opacity": 0.5,
                      }}
                      filter={filter}
                    />
                  </Source>
                )}
                {safeLayerOn && safeData && (
                  <Source id="safe-source" type="geojson" data={safeData}>
                    <Layer
                      id="safe"
                      type="fill"
                      paint={{
                        "fill-color": "orange",
                        "fill-opacity": 0.5,
                      }}
                    />
                  </Source>
                )}
                {floodplainLayerOn && floodplainData && (
                  <Source
                    id="floodplain-source"
                    type="geojson"
                    data={floodplainData}
                  >
                    <Layer
                      id="floodplain"
                      type="fill"
                      paint={{
                        "fill-color": "blue",
                        "fill-opacity": 0.5,
                      }}
                    />
                  </Source>
                )}
                {waterLayerOn && waterData && (
                  <Source id="water-source" type="geojson" data={waterData}>
                    <Layer
                      id="water"
                      type="fill"
                      paint={{
                        "fill-color": "cyan",
                        "fill-opacity": 0.5,
                      }}
                    />
                  </Source>
                )}
                {fieldDetails.fieldRate.rows.map((soil) => {
                  const soilGeometry = JSON.parse(soil.geometry);
                  const [lng, lat] = getMarkerLocation(soilGeometry);
                  return (
                    <>
                      {soilLayerOn && (
                        <Marker longitude={lng} latitude={lat} anchor="center">
                          <label style={{ color: "white" }}>{soil.musym}</label>
                        </Marker>
                      )}
                    </>
                  );
                })}
              </Map>
            </div>
          </div>
          <div className="report-rental-table-container">
            <h3
              className={
                printList
                  ? "report-section-header print"
                  : "report-section-header"
              }
            >
              Base Rental Rate and Breakdown Table<sup>[1]</sup>:
            </h3>

            <p className="report-table-information">
              This is a breakdown table of the soil types and corresponding
              rental rate regarding your field. Please find more about soil
              types in your county from{" "}
              <a
                href="https://www.nrcs.usda.gov/publications/Map%20Unit%20Description%20%28Brief%2C%20Generated%29%20Query%20by%20Soil%20Survey%20Area%20and%20Map%20Unit%20%28All%20Components%29.html"
                target="_blank"
                style={{ color: "maroon" }}
                rel="noreferrer"
              >
                here
              </a>
              .
            </p>
            <Table
              className={printList ? "print report-table" : "report-table"}
              columns={columns}
              dataSource={soilTableData?.slice(0, 5)}
              pagination={false}
              rowClassName={(record) =>
                record.key === highlightFeatureId
                  ? "report-table-highlight"
                  : " "
              }
            />
          </div>
          <div className="report-practice-container">
            <h3
              className={
                printList
                  ? "print report-section-header"
                  : "report-section-header"
              }
            >
              Eligible CRP Practices:
            </h3>

            {scoredAndSortedPractices.map((practice, index) => {
              const practiceTitle = practice.practiceNumber;
              const printPractice = practiceTitle;

              const printSignups = {
                G: practice.programs?.some(
                  (p) =>
                    p.name === "General Signup" &&
                    p.period !== false &&
                    p.period !== "false"
                ),
                C: true,
              };

              const printIncentives = practice.programs?.reduce(
                (acc, program) => {
                  const incentives = program.incentives || {};

                  const parseValue = (val) =>
                    val !== undefined &&
                    val !== "false" &&
                    val !== false &&
                    !isNaN(Number(val))
                      ? Number(val)
                      : null;

                  const sip = parseValue(incentives.sip);
                  const pip = parseValue(incentives.pip);
                  const rri = parseValue(incentives.rentalRateIncentive);
                  const rra = parseValue(incentives.rentalRateAdj);
                  const clear = parseValue(incentives.waterqualitythruclear30);

                  if (sip !== null) acc.S = Math.max(acc.S ?? 0, sip);
                  if (pip !== null) acc.P = Math.max(acc.P ?? 0, pip);
                  if (rri !== null) acc.R = Math.max(acc.R ?? 0, rri);
                  if (rra !== null) acc.A = Math.max(acc.A ?? 0, rra);
                  if (clear !== null) acc.W = Math.max(acc.W ?? 0, clear);

                  return acc;
                },
                { S: null, P: null, R: null, A: null, W: null }
              );

              const getEstimatedPayment = (paymentMultiplier) => {
                const totalFieldPayment = Math.round(
                  fieldRentalRate *
                    (printIncentives.S ? 1.325 : 1) *
                    (printIncentives.R ? 1.2 : 1) *
                    (printIncentives.P ? 1.5 : 1) *
                    paymentMultiplier
                );
                const maximumFieldPayment =
                  paymentMultiplier === 0.9
                    ? Math.round(300 * fieldSize)
                    : paymentMultiplier === 0.85
                    ? Math.round(240 * fieldSize)
                    : totalFieldPayment;

                return Math.min(totalFieldPayment, maximumFieldPayment);
              };

              return (
                <div
                  key={practiceTitle}
                  className="print report-practice-detail-container"
                >
                  <PrintHelp
                    practice={practice}
                    currentData={practice}
                    currentPriorities={practice.priorities}
                    selectedTopPracticeList={selectedTopPracticeList}
                    selectedFips={selectedFips}
                    listView="Compact"
                    preferredPractice={printPractice}
                    setPreferredPractice={setPreferredPractice}
                    setPreferredSignups={setPreferredSignups}
                    setPreferredIncentives={setPreferredIncentives}
                    userFavoritePractices={userFavoritePractices}
                    printView={true}
                  />

                  <div className="print report-practice-list-short">
                    <div className="print report-practice-detail">
                      <h3>
                        Your Estimated Field Rental Rate is{" "}
                        {printSignups.G && (
                          <span>
                            ${getEstimatedPayment(0.85)} (General Signup){" "}
                            <sup>[1]</sup>
                          </span>
                        )}
                        {printSignups.G && printSignups.C ? " or " : ""}
                        {printSignups.C && (
                          <span>
                            ${getEstimatedPayment(0.9)} (Continuous Signup){" "}
                            <sup>[1]</sup>
                          </span>
                        )}
                      </h3>
                      <p>
                        As determined by USDA, landowners will receive 85% for
                        general and 90% for continuous signup. By signing up for{" "}
                        <b>{printPractice}</b>
                        {(printIncentives.S ||
                          printIncentives.R ||
                          printIncentives.P) && <> you are eligible for </>}
                        {printIncentives.S && (
                          <span>a 32.5% signup incentive, </span>
                        )}
                        {printIncentives.R && (
                          <span>a 20% rental rate incentive, </span>
                        )}
                        {printIncentives.P && (
                          <span>a 50% practice incentive, </span>
                        )}
                        and <b>{printPractice}</b> supports{" "}
                        {!printSignups.C ? (
                          <>
                            general signup, with a cap of $240/acre, yielding
                            approx. <b>${getEstimatedPayment(0.85)}</b>.
                          </>
                        ) : !printSignups.G ? (
                          <>
                            continuous signup, with a cap of $300/acre, yielding
                            approx. <b>${getEstimatedPayment(0.9)}</b>.
                          </>
                        ) : (
                          "both general and continuous signup."
                        )}
                      </p>
                    </div>
                    {practice.crepInfo?.length > 0 &&
                      practice.crepInfo.map((option, index) => (
                        <div
                          key={index}
                          className="report-practice-list-short"
                          style={{
                            backgroundColor: `${
                              crepColorMap?.get(option.crepname) || "#eee"
                            }50`,
                          }}
                        >
                          <h3>
                            Estimated First-Year Payment for{" "}
                            <b>{option.crepname}</b>:{" "}
                            <span>
                              $
                              {Math.round(
                                getEstimatedPayment(1) *
                                  (parseInt(option.newenrollmentsrr) / 100) +
                                  parseInt(option.stateincentive) * fieldSize
                              )}{" "}
                              <sup>[1]</sup>
                            </span>
                          </h3>

                          <p>
                            Enrollment through <b>{option.crepname}</b> includes
                            additional incentives:
                            <ul>
                              <li>
                                <b>{option.newenrollmentsrr}%</b> soil rental
                                rate for new enrollments
                              </li>
                              <li>
                                <b>${option.stateincentive}/acre</b> annual
                                state incentive
                              </li>
                            </ul>
                            This results in an estimated potential first-year
                            payment of{" "}
                            <b>
                              $
                              {Math.round(
                                getEstimatedPayment(1) *
                                  (parseInt(option.newenrollmentsrr) / 100) +
                                  parseInt(option.stateincentive) * fieldSize
                              )}
                            </b>{" "}
                            <sup>[1]</sup> for your field.
                          </p>
                        </div>
                      ))}
                    <div
                      style={{ marginTop: "0", paddingTop: "0" }}
                      key={index}
                      className="report-practice-list-short"
                    >
                      <p>
                        Please contact your local FSA office for more details
                        and official offers.
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <hr className="report-hr" />
          <div className="report-general-info-container">
            <div>
              <h3
                className={
                  printList
                    ? "report-section-header print"
                    : "report-section-header"
                }
              >
                Local USDA Office:
              </h3>
              <div
                className={
                  printList
                    ? "report-usda-information print"
                    : "report-usda-information"
                }
              >
                <p>
                  Please reach out to your local USDA Farm Service Agency office
                  for further information.
                </p>
                <Table
                  className={printList ? "print report-table" : "report-table"}
                  columns={[
                    {
                      title: "Service Center",
                      dataIndex: "service_center",
                      key: "service_center",
                      align: "center",
                    },
                    {
                      title: "Address",
                      dataIndex: "physical_address",
                      key: "physical_address",
                      align: "center",
                    },
                    {
                      title: "Phone",
                      dataIndex: "phone",
                      key: "phone",
                      align: "center",
                    },
                  ]}
                  dataSource={serviceCenterList}
                  pagination={false}
                  size="large"
                />
              </div>
            </div>
            <div>
              <h3
                className={
                  printList
                    ? "report-section-header print"
                    : "report-section-header"
                }
              >
                Disclaimer:
              </h3>
              <div
                className={
                  printList ? "report-disclaimer print" : "report-disclaimer"
                }
              >
                <p>
                  [1] Please note that the estimated rental rate is a rough
                  estimation based on the CRP practice you selected. It may vary
                  due to your regional USDA official's final decision.
                </p>
                <p>
                  [2] This material is based upon work supported by the U.S.
                  Department of Agriculture, Farm Production and Conservation
                  program under agreement number FBC22CPT0012776. Any opinions,
                  findings, conclusions, or recommendations expressed in this
                  publication are those of the author(s) and do not necessarily
                  reflect the views of the U.S. Department of Agriculture. In
                  addition, any reference to specific brands or types of
                  products or services does not constitute or imply an
                  endorsement by the U.S. Department of Agriculture for those
                  products or services. USDA is an equal opportunity provider,
                  employer, and lender
                  (https://www.usda.gov/non-discrimination-statement). This work
                  is produced with support from the Mississippi State University
                  Forest and Wildlife Research Center and Mississippi State
                  University Geosystems Research Institute.
                </p>
                <p>
                  * All dollar amounts presented are approximations derived from
                  soil type and rental rate data available at the time of this
                  tool's publication.
                </p>
              </div>
            </div>
          </div>
          <Modal
            centered={true}
            title="Save My Search"
            open={isSavePopOpen}
            onOk={null}
            footer={null}
            onCancel={saveSearchPopClose}
          >
            <Form
              name="basic"
              labelCol={{
                span: 8,
              }}
              form={form}
              wrapperCol={{
                span: 16,
              }}
              initialValues={{
                remember: true,
              }}
              onFinish={storeSearchData}
              // onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              <Form.Item
                label="Save Search"
                name="SaveKey"
                rules={[
                  {
                    required: true,
                    message: "Please input your Save Search!",
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                wrapperCol={{
                  offset: 8,
                  span: 16,
                }}
              >
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </Spin>
      <Footer />
    </>
  );
};

export default FieldPrint;
